<template>
  <div class="ag-studio-tab">
    <SnackbarComp :SnackbarComponent="snackbar" />

    <ConfigureActionCredentialsDialog
      v-model="showCredentialsDialog"
      :record="selectedRecord"
      @saved="onCredentialsSaved"
      @error="showError"
    />

    <header class="ag-actions-toolbar">
      <div>
        <h3 class="int-header__title">Integrations</h3>
        <p class="int-header__subtitle">
          Ready-made actions from Yandle -- just add your credentials to switch one on.
        </p>
      </div>
      <v-btn
        icon
        variant="text"
        size="small"
        :loading="loading"
        :disabled="!isSessionReady"
        aria-label="Refresh integrations"
        @click="loadActions"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </header>

    <div v-if="loading" class="ag-studio-tab__loading d-flex align-center justify-center">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <template v-else-if="!integrationGroups.length">
      <div class="ag-empty">
        <div class="ag-empty__orb" aria-hidden="true">
          <v-icon icon="mdi-puzzle-outline" size="42" />
        </div>
        <h3 class="ag-empty__title">No integrations available yet</h3>
        <p class="ag-empty__text">
          Yandle-built integrations will show up here as they're released.
        </p>
      </div>
    </template>

    <template v-else>
      <div class="int-grid">
        <div v-for="group in integrationGroups" :key="group.template_id" class="int-card">
          <div class="int-card__head">
            <div class="int-card__logo">
              <img v-if="group.logo_url" :src="group.logo_url" :alt="group.template_name" />
              <v-icon v-else icon="mdi-puzzle-outline" size="24" />
            </div>
            <div class="int-card__head-text">
              <div class="int-card__name">{{ group.template_name }}</div>
              <span
                class="int-card__status"
                :class="group.is_active ? 'int-card__status--on' : 'int-card__status--off'"
              >
                {{ group.is_active ? 'Configured' : 'Not configured' }}
              </span>
            </div>
          </div>

          <p class="int-card__desc">{{ group.template_description }}</p>

          <ul v-if="group.variant_names.length > 1" class="int-card__variants">
            <li v-for="name in group.variant_names" :key="name">{{ name }}</li>
          </ul>

          <div class="int-card__footer">
            <v-btn
              size="small"
              variant="flat"
              :color="group.is_active ? 'default' : 'primary'"
              @click="openConfigure(group)"
            >
              {{ group.is_active ? 'Manage' : 'Configure' }}
            </v-btn>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, toRef, computed } from 'vue'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import ConfigureActionCredentialsDialog from '@/components/Agent/OnCall/ConfigureActionCredentialsDialog.vue'
import {
  useOnCallActionsData,
  getOnCallActionsErrorMessage,
} from '@/composables/agent/useOnCallActionsData'
import { getS3PresignedReadUrl } from '@/utils/orgDetailsApi'
import { fetchActionTemplate } from '@/api/onCallActionsApi'
import { isApiSuccess, getApiMessage } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'IntegrationsTab' })

const props = defineProps({
  active: { type: Boolean, default: false },
})

const emit = defineEmits(['counts-changed'])

const snackbar = ref({})
const showCredentialsDialog = ref(false)
const selectedRecord = ref(null)
const templateMeta = ref({})
const resolvedLogoUrls = ref({})

const activeRef = toRef(props, 'active')

function showSnack(color, text) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor: color,
    SnackbarText: text,
  }
}

function showError(payload) {
  const text = typeof payload === 'string'
    ? payload
    : getOnCallActionsErrorMessage(payload)
  showSnack('error', text)
}

const {
  loading,
  isSessionReady,
  actionRows,
  loadActions,
} = useOnCallActionsData(activeRef, { onError: showError })

const templateActions = computed(() => actionRows.value.filter((row) => row.template_id))

const integrationGroups = computed(() => {
  const byTemplate = new Map()
  templateActions.value.forEach((action) => {
    if (!byTemplate.has(action.template_id)) byTemplate.set(action.template_id, [])
    byTemplate.get(action.template_id).push(action)
  })

  return Array.from(byTemplate.entries()).map(([templateId, actions]) => {
    const meta = templateMeta.value[templateId] || {}
    return {
      template_id: templateId,
      template_name: meta.template_name || actions[0]?.action_name || 'Integration',
      template_description: meta.template_description || '',
      logo_url: meta.logo_s3_key ? resolvedLogoUrls.value[meta.logo_s3_key] || '' : '',
      is_active: actions.every((action) => action.is_active),
      variant_names: actions.map((action) => action.action_name),
      primary_action: actions[0],
    }
  })
})

async function loadTemplateMeta() {
  const ids = [...new Set(templateActions.value.map((row) => row.template_id))]
  const missing = ids.filter((id) => !templateMeta.value[id])
  if (!missing.length) return

  const results = await Promise.all(
    missing.map(async (templateId) => {
      try {
        const response = await fetchActionTemplate({ template_id: templateId })
        if (isApiSuccess(response)) return [templateId, response.data]
      } catch {
        // best-effort -- card falls back to action name
      }
      return [templateId, null]
    }),
  )

  const next = { ...templateMeta.value }
  results.forEach(([templateId, data]) => {
    if (data) next[templateId] = data
  })
  templateMeta.value = next

  const logoKeys = [...new Set(Object.values(next).map((meta) => meta?.logo_s3_key).filter(Boolean))]
  const missingLogos = logoKeys.filter((key) => !resolvedLogoUrls.value[key])
  if (!missingLogos.length) return

  const logoResults = await Promise.all(
    missingLogos.map(async (key) => {
      try {
        return [key, await getS3PresignedReadUrl(key)]
      } catch {
        return [key, null]
      }
    }),
  )
  const nextLogos = { ...resolvedLogoUrls.value }
  logoResults.forEach(([key, url]) => {
    if (url) nextLogos[key] = url
  })
  resolvedLogoUrls.value = nextLogos
}

async function loadAll() {
  await loadActions()
  await loadTemplateMeta()
}

function openConfigure(group) {
  selectedRecord.value = { ...group.primary_action }
  showCredentialsDialog.value = true
}

function onCredentialsSaved() {
  showSnack('success', 'Integration configured successfully.')
  void loadAll()
  emit('counts-changed')
}

loadAll()
</script>

<style scoped>
.int-header__title {
  font-weight: 650;
  font-size: 1.05rem;
  margin: 0;
}
.int-header__subtitle {
  font-size: 0.82rem;
  color: #64748b;
  margin: 2px 0 0;
}
.int-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  margin-top: 16px;
}
.int-card {
  border: 1px solid rgba(100, 116, 139, 0.18);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: var(--v-theme-surface, #fff);
}
.int-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.int-card__logo {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(99, 102, 241, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}
.int-card__logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.int-card__name {
  font-weight: 650;
  font-size: 0.95rem;
}
.int-card__status {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  margin-top: 2px;
}
.int-card__status--on {
  color: #059669;
  background: rgba(5, 150, 105, 0.12);
}
.int-card__status--off {
  color: #d97706;
  background: rgba(217, 119, 6, 0.12);
}
.int-card__desc {
  font-size: 0.82rem;
  color: #64748b;
  margin: 0;
  flex: 1;
}
.int-card__variants {
  margin: 0;
  padding-left: 18px;
  font-size: 0.78rem;
  color: #94a3b8;
}
.int-card__footer {
  display: flex;
  justify-content: flex-end;
}
</style>
